import React, { useCallback, useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/ihthisabi/AuthContext'
import ConfirmationModal from './ConfirmationModal'
import PoweredByD4DX from '../sidebars/PoweredByD4DX'
import BrandLogo from '../branding/BrandLogo'
import useModalFocus from '../../hooks/useModalFocus'
import { 
  Home, 
  FileText, 
  BarChart3, 
  User, 
  LogOut, 
  Menu,
  Users,
  ClipboardList,
  LifeBuoy,
  Settings,
  MessageSquare,
  Globe,
  Database,
  Archive,
  MapPin,
  ChevronDown,
  ChevronRight,
  ArrowLeftRight,
  UserCog
} from 'lucide-react'

const Layout = () => {
  const { user, logout, switchRole } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)
  const closeMore = useCallback(() => setMoreOpen(false), [])
  const { dialogRef: moreSheetRef, triggerRef: moreButtonRef } = useModalFocus(moreOpen, closeMore)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  const [switchingRole, setSwitchingRole] = useState(false)

  // Other roles this RUKN ID holds (multi-role users only)
  const otherRoles = (user?.availableRoles || []).filter(r => r.role !== user?.role)

  const roleDashboardPath = {
    admin: '/ihthisabi/admin',
    districtAdmin: '/ihthisabi/districtadmin',
    unitAdmin: '/ihthisabi/unitadmin',
    mekhalaNazim: '/ihthisabi/mekhalanazim',
    rukn: '/ihthisabi/dashboard'
  }

  const handleSwitchRole = async (targetRole) => {
    if (switchingRole) return
    setSwitchingRole(true)
    const result = await switchRole(targetRole)
    setSwitchingRole(false)
    if (result.success) {
      setProfileMenuOpen(false)
      navigate(roleDashboardPath[result.user.role] || '/ihthisabi/dashboard', { replace: true })
    }
  }

  const handleLogout = () => {
    setShowLogoutModal(true)
  }

  const confirmLogout = () => {
    logout()
    navigate('/ihthisabi/login')
    setShowLogoutModal(false)
  }

  // A nav item is active only if it is the *best* (longest) matching route for the
  // current path. Plain prefix matching made the dashboard href (e.g. /ihthisabi/admin)
  // stay active on every /ihthisabi/admin/* sub-route. bestMatchHref (computed below)
  // resolves this by picking the single most-specific destination.
  const isActive = (href) => href != null && href === bestMatchHref

  const isGroupActive = (children) =>
    children.some(child => isActive(child.href))

  // Auto-expand groups that contain the active route
  useEffect(() => {
    const groups = buildNavigation()
    const toExpand = new Set()
    groups.forEach(item => {
      if (item.type === 'group' && isGroupActive(item.children)) {
        toExpand.add(item.name)
      }
    })
    if (toExpand.size > 0) {
      setExpandedGroups(prev => new Set([...prev, ...toExpand]))
    }
    setMoreOpen(false)
  }, [location.pathname])

  // Close profile menu / More sheet on Escape
  useEffect(() => {
    if (!profileMenuOpen && !moreOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setProfileMenuOpen(false)
        setMoreOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [profileMenuOpen, moreOpen])

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  const isSuperAdmin = user?.isAdmin || user?.role === 'mainAdmin'

  const dashboardHref =
    user?.role === 'admin' ? '/ihthisabi/admin'
    : user?.role === 'unitAdmin' ? '/ihthisabi/unitadmin'
    : user?.role === 'districtAdmin' ? '/ihthisabi/districtadmin'
    : user?.role === 'mekhalaNazim' ? '/ihthisabi/mekhalanazim'
    : '/ihthisabi/dashboard'

  const buildNavigation = () => {
    if (user?.role === 'rukn') {
      return [
        { name: 'Dashboard', mobileName: 'Dashboard', href: dashboardHref, icon: Home },
        { name: 'Submit Form', mobileName: 'Submit', href: '/ihthisabi/submit', icon: FileText },
        { name: 'Profile', mobileName: 'Profile', href: '/ihthisabi/profile', icon: User },
        { name: 'Help Desk', mobileName: 'Help', href: '/ihthisabi/help-desk', icon: LifeBuoy },
      ]
    }

    if (user?.role === 'unitAdmin') {
      return [
        { name: 'Dashboard', mobileName: 'Dashboard', href: dashboardHref, icon: Home },
        { name: 'Unit Submissions', mobileName: 'Forms', href: '/ihthisabi/unitadmin/submissions', icon: BarChart3 },
        { name: 'Unit Members', mobileName: 'Members', href: '/ihthisabi/unitadmin/members', icon: Users },
        { name: 'Profile', mobileName: 'Profile', href: '/ihthisabi/profile', icon: User },
        { name: 'Help Desk', mobileName: 'Help', href: '/ihthisabi/help-desk', icon: LifeBuoy },
      ]
    }

    if (user?.role === 'districtAdmin') {
      return [
        { name: 'Dashboard', mobileName: 'Dashboard', href: dashboardHref, icon: Home },
        { name: 'District Submissions', mobileName: 'Forms', href: '/ihthisabi/districtadmin/submissions', icon: BarChart3 },
        { name: 'District Members', mobileName: 'Members', href: '/ihthisabi/districtadmin/members', icon: Users },
        { name: 'Profile', mobileName: 'Profile', href: '/ihthisabi/profile', icon: User },
        { name: 'Help Desk', mobileName: 'Help', href: '/ihthisabi/help-desk', icon: LifeBuoy },
      ]
    }

    // Mekhala Nazim sees only their own assigned reports — no region submissions,
    // member lists or consolidation.
    if (user?.role === 'mekhalaNazim') {
      return [
        { name: 'My Reports', mobileName: 'Reports', href: dashboardHref, icon: FileText },
        { name: 'Profile', mobileName: 'Profile', href: '/ihthisabi/profile', icon: User },
        { name: 'Help Desk', mobileName: 'Help', href: '/ihthisabi/help-desk', icon: LifeBuoy },
      ]
    }

    if (user?.role === 'admin') {
      return [
        { name: 'Dashboard', mobileName: 'Home', href: '/ihthisabi/admin', icon: Home },
        {
          name: 'Submissions',
          mobileName: 'Forms',
          icon: BarChart3,
          type: 'group',
          children: [
            { name: 'All Submissions', href: '/ihthisabi/admin/submissions', icon: BarChart3 },
            { name: 'Consolidation', href: '/ihthisabi/admin/consolidation', icon: BarChart3 },
            { name: 'Unit Reply', href: '/ihthisabi/admin/unit-reply', icon: MessageSquare },
          ]
        },
        { name: 'Form Management', mobileName: 'Forms', href: '/ihthisabi/admin/form-management', icon: ClipboardList },
        { name: 'Report Management', mobileName: 'Reports', href: '/ihthisabi/admin/dynamic-forms', icon: FileText },
        { name: 'Mekhala Nazim', mobileName: 'Nazim', href: '/ihthisabi/admin/mekhala-nazims', icon: UserCog },
        {
          name: 'User Management',
          mobileName: 'Users',
          icon: Users,
          type: 'group',
          children: [
            { name: 'All Members', href: '/ihthisabi/admin/members', icon: Users },
            { name: 'Unit Admins', href: '/ihthisabi/admin/unit-admins', icon: Settings },
            { name: 'District Admins', href: '/ihthisabi/admin/district-admins', icon: Settings },
            ...(isSuperAdmin ? [
              { name: 'User Management', href: '/ihthisabi/admin/user-management', icon: Settings },
              { name: 'Archive', href: '/ihthisabi/admin/archive', icon: Archive },
            ] : []),
            { name: 'Abroad Countries', href: '/ihthisabi/admin/abroad-countries', icon: Globe },
            { name: 'Abroad Members', href: '/ihthisabi/admin/abroad-members', icon: MapPin },
          ]
        },
        { name: 'Master Data', mobileName: 'Data', href: '/ihthisabi/admin/master-data', icon: Database },
        { name: 'Profile', mobileName: 'Profile', href: '/ihthisabi/profile', icon: User },
      ]
    }

    return [
      { name: 'Dashboard', mobileName: 'Dashboard', href: dashboardHref, icon: Home },
      { name: 'Profile', mobileName: 'Profile', href: '/ihthisabi/profile', icon: User },
    ]
  }

  const navigation = buildNavigation()

  // Longest nav href that matches the current path (see isActive above).
  const bestMatchHref = (() => {
    const currentPath = location.pathname
    const leaves = []
    navigation.forEach(item => {
      if (item.type === 'group') item.children.forEach(c => leaves.push(c.href))
      else if (item.href) leaves.push(item.href)
    })
    let best = null
    for (const href of leaves) {
      if (currentPath === href || currentPath.startsWith(href + '/')) {
        if (!best || href.length > best.length) best = href
      }
    }
    return best
  })()

  // Curated mobile bottom nav — a minimal set of the most important destinations
  // plus a "More" button that opens the full sidebar. `match` lets a tab stay
  // highlighted across a section's related sub-routes. Account destinations
  // (Profile, Help Desk) are deliberately absent — see accountPaths below.
  const buildBottomNav = () => {
    if (user?.role === 'rukn') {
      return [
        { name: 'Home', href: dashboardHref, icon: Home },
        {
          name: 'Submit',
          href: '/ihthisabi/submit',
          icon: FileText,
          activePaths: [
            '/ihthisabi/submit',
            '/ihthisabi/submission-success',
            '/ihthisabi/submissions',
            '/ihthisabi/alternative-submission',
            '/ihthisabi/alternative-submissions',
          ],
        },
      ]
    }
    if (user?.role === 'unitAdmin') {
      return [
        { name: 'Home', href: dashboardHref, icon: Home },
        { name: 'Forms', href: '/ihthisabi/unitadmin/submissions', icon: BarChart3 },
        { name: 'Members', href: '/ihthisabi/unitadmin/members', icon: Users },
        { name: 'More', icon: Menu, action: 'menu' },
      ]
    }
    if (user?.role === 'districtAdmin') {
      return [
        { name: 'Home', href: dashboardHref, icon: Home },
        { name: 'Forms', href: '/ihthisabi/districtadmin/submissions', icon: BarChart3 },
        { name: 'Members', href: '/ihthisabi/districtadmin/members', icon: Users },
        { name: 'More', icon: Menu, action: 'menu' },
      ]
    }
    if (user?.role === 'mekhalaNazim') {
      return [
        { name: 'Reports', href: dashboardHref, icon: FileText },
      ]
    }
    if (user?.role === 'admin') {
      return [
        { name: 'Home', href: '/ihthisabi/admin', icon: Home, match: ['/ihthisabi/admin'] },
        { name: 'Reports', href: '/ihthisabi/admin/submissions', icon: BarChart3,
          match: ['/ihthisabi/admin/submissions', '/ihthisabi/admin/consolidation', '/ihthisabi/admin/unit-reply'] },
        { name: 'Forms', href: '/ihthisabi/admin/form-management', icon: ClipboardList },
        { name: 'Users', href: '/ihthisabi/admin/members', icon: Users,
          match: ['/ihthisabi/admin/members', '/ihthisabi/admin/unit-admins', '/ihthisabi/admin/district-admins', '/ihthisabi/admin/user-management',
                  '/ihthisabi/admin/archive', '/ihthisabi/admin/abroad-countries', '/ihthisabi/admin/abroad-members'] },
        { name: 'More', icon: Menu, action: 'menu' },
      ]
    }
    return [
      { name: 'Home', href: dashboardHref, icon: Home },
    ]
  }

  // Profile and Help Desk are account destinations: they live only in the
  // header avatar menu (same as the members portal), never in the bottom bar
  // or its "More" sheet. The filter below keeps that true for every role.
  const accountPaths = new Set(['/ihthisabi/profile', '/ihthisabi/help-desk'])
  const bottomNavItems = buildBottomNav().filter(item => !accountPaths.has(item.href))

  // "More" lists only what the bottom bar doesn't already carry — repeating the
  // four tabs that are one tap away is what made the old drawer feel redundant.
  const moreItems = (() => {
    const inBar = new Set([...accountPaths, ...bottomNavItems.filter(i => i.href).map(i => i.href)])
    const rest = []
    navigation.forEach(item => {
      if (item.type === 'group') {
        item.children.forEach(child => { if (!inBar.has(child.href)) rest.push(child) })
      } else if (item.href && !inBar.has(item.href)) {
        rest.push(item)
      }
    })
    return rest
  })()

  const pathMatches = (path) => (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  )

  const matchesBottomDestination = (item) => {
    if (item.activePaths) return item.activePaths.some(pathMatches)
    if (item.match) return item.match.includes(bestMatchHref)
    return isActive(item.href)
  }

  const hasActiveBottomDestination = bottomNavItems.some((item) => {
    if (item.action === 'menu') return false
    return matchesBottomDestination(item)
  })
  const hasActiveMoreItem = !hasActiveBottomDestination && moreItems.some((item) => isActive(item.href))

  const isBottomActive = (item) => {
    if (item.action === 'menu') return moreOpen || hasActiveMoreItem
    return matchesBottomDestination(item)
  }

  const handleBottomClick = (item) => {
    if (item.action === 'menu') {
      setMoreOpen(prev => !prev)
    } else {
      setMoreOpen(false)
      navigate(item.href)
    }
  }

  const roleLabel = user?.role === 'unitAdmin'
    ? 'Unit Admin'
    : user?.role === 'districtAdmin'
      ? 'District Admin'
      : user?.role === 'mekhalaNazim'
        ? 'Mekhala Nazim'
        : user?.role === 'admin'
          ? 'Admin'
          : 'Member'

  // Mobile app-bar title. Pages hide their own <h1> on phones, so the bar is the
  // only thing that names the screen — longest matching prefix wins.
  const PAGE_TITLES = {
    '/ihthisabi/admin/submissions': 'All Submissions',
    '/ihthisabi/admin/consolidation': 'Consolidation',
    '/ihthisabi/admin/unit-reply': 'Unit Reply',
    '/ihthisabi/admin/form-management': 'Form Management',
    '/ihthisabi/admin/members': 'All Members',
    '/ihthisabi/admin/unit-admins': 'Unit Admins',
    '/ihthisabi/admin/district-admins': 'District Admins',
    '/ihthisabi/admin/user-management': 'User Management',
    '/ihthisabi/admin/archive': 'Archive',
    '/ihthisabi/admin/abroad-countries': 'Abroad Countries',
    '/ihthisabi/admin/abroad-members': 'Abroad Members',
    '/ihthisabi/admin/master-data': 'Master Data',
    '/ihthisabi/admin/mekhala-nazims': 'Mekhala Nazim',
    '/ihthisabi/admin/dynamic-forms': 'Report Management',
    '/ihthisabi/admin': 'Admin Dashboard',
    '/ihthisabi/mekhalanazim/reports': 'Submit Report',
    '/ihthisabi/mekhalanazim/submissions': 'Submission',
    '/ihthisabi/mekhalanazim': 'My Reports',
    '/ihthisabi/unitadmin/submissions': 'Unit Submissions',
    '/ihthisabi/unitadmin/members': 'Unit Members',
    '/ihthisabi/unitadmin/details': 'Unit Details',
    '/ihthisabi/unitadmin/submit-form': 'Submit Report',
    '/ihthisabi/unitadmin/submission-details': 'Submission',
    '/ihthisabi/unitadmin': 'Unit Dashboard',
    '/ihthisabi/districtadmin/submissions': 'District Submissions',
    '/ihthisabi/districtadmin/members': 'District Members',
    '/ihthisabi/districtadmin/areas': 'Area Details',
    '/ihthisabi/districtadmin': 'District Dashboard',
    '/ihthisabi/alternative-submissions': 'Alternative Submission',
    '/ihthisabi/alternative-submission': 'Alternative Submission',
    '/ihthisabi/submission-success': 'Submitted',
    '/ihthisabi/submissions': 'Submission',
    '/ihthisabi/submit': 'Submit Report',
    '/ihthisabi/dashboard': 'Dashboard',
    '/ihthisabi/profile': 'Profile',
    '/ihthisabi/help-desk': 'Help Desk',
  }

  const pageTitle = (() => {
    const current = location.pathname
    let best = null
    Object.keys(PAGE_TITLES).forEach(prefix => {
      if (current === prefix || current.startsWith(prefix + '/')) {
        if (!best || prefix.length > best.length) best = prefix
      }
    })
    return best ? PAGE_TITLES[best] : 'IHTHISABI'
  })()

  // Secondary line: who/where the user is. `unit` is '-' for members without a unit.
  const contextLabel =
    (user?.unit && user.unit !== '-' ? user.unit : null) ||
    user?.mekhalaName ||
    user?.name || user?.username || roleLabel

  const renderNavItem = (item) => {
    if (item.type === 'group') {
      const Icon = item.icon
      const groupActive = isGroupActive(item.children)
      const expanded = expandedGroups.has(item.name)
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleGroup(item.name)}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
              groupActive
                ? 'text-white bg-white/10'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${groupActive ? 'text-white' : 'text-white/50'}`} />
            <span className="flex-1 text-left">{item.name}</span>
            {expanded
              ? <ChevronDown className="w-4 h-4 text-white/50 flex-shrink-0" />
              : <ChevronRight className="w-4 h-4 text-white/50 flex-shrink-0" />
            }
          </button>
          {expanded && (
            <div className="mt-1 ml-4 pl-4 border-l border-white/10 space-y-1 pb-1">
              {item.children.map(child => {
                const ChildIcon = child.icon
                const active = isActive(child.href)
                return (
                  <button
                    key={child.href}
                    onClick={() => navigate(child.href)}
                    className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                      active
                        ? 'bg-[#7B4FF2] text-white shadow-lg shadow-[#7B4FF2]/30'
                        : 'text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <ChildIcon className={`w-4 h-4 mr-2.5 flex-shrink-0 ${active ? 'text-white' : 'text-white/40'}`} />
                    <span>{child.name}</span>
                    {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/50" />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    const Icon = item.icon
    const active = isActive(item.href)
    return (
      <button
        key={item.name}
        onClick={() => navigate(item.href)}
        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
          active
            ? 'bg-[#7B4FF2] text-white shadow-lg shadow-[#7B4FF2]/30 transform scale-[1.02]'
            : 'text-white/80 hover:bg-white/10 hover:text-white hover:translate-x-1'
        }`}
      >
        <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${active ? 'text-white' : 'text-white/50'}`} />
        <span>{item.name}</span>
        {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/50" />}
      </button>
    )
  }

  return (
    <div className="app-viewport bg-gray-50 flex">
      {/* Sidebar — desktop only; phones navigate via the bottom bar and its
          "More" panel, so this never slides in on mobile. */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 -translate-x-full bg-gradient-to-b from-[#1E1040] to-[#2D1B69] border-r border-[#3D2475] shadow-xl lg:translate-x-0">
        {/* Sidebar Header */}
        <div className="flex items-center h-16 px-6 border-b border-[#3D2475] bg-[#1A0D3D]/50 backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <BrandLogo variant="white" alt="IHTHISABI Logo" size="sm" className="h-10" />
            <div>
              <span className="text-sm font-bold text-white block">IHTHISABI</span>
              <span className="text-xs text-purple-300">REPORT</span>
            </div>
          </div>
        </div>

        {/* Sidebar content */}
        <div className="h-[calc(100vh-4rem)] flex flex-col">
          <nav className="mt-6 px-3 flex-1 overflow-y-auto scrollbar-hide">
            <div className="space-y-1 pb-4">
              {navigation.map(item => renderNavItem(item))}
            </div>
          </nav>

          {/* Powered by and logout at bottom */}
          <div className="mt-auto p-4 border-t border-white/10 bg-[#1A0D3D]/30 backdrop-blur-sm">
            {/* Role switcher — only for users holding multiple roles */}
            {otherRoles.length > 0 && (
              <div className="mb-3">
                <p className="px-1 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-purple-300/70">
                  Switch Role
                </p>
                <div className="space-y-1">
                  {otherRoles.map((roleOption) => (
                    <button
                      key={roleOption.role}
                      onClick={() => handleSwitchRole(roleOption.role)}
                      disabled={switchingRole}
                      className="w-full flex items-center px-3 py-2 text-xs font-medium text-white/80 bg-white/5 hover:bg-[#7B4FF2] hover:text-white rounded-lg transition-all duration-200 border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <ArrowLeftRight className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">
                        {roleOption.label}
                        {roleOption.scope ? ` · ${roleOption.scope}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <PoweredByD4DX dark />
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white/80 bg-white/5 hover:bg-red-600 hover:text-white rounded-xl transition-all duration-200 border border-white/10 hover:border-red-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden lg:ml-64">
        <div className="app-mobile-header lg:hidden flex-shrink-0 z-20 bg-white/80 backdrop-blur-xl backdrop-saturate-150 shadow-[0_1px_0_rgba(16,24,40,0.06)]">
          <div className="flex items-center justify-between gap-2.5 px-3.5 py-2.5">
            <div className="min-w-0 flex items-center gap-2.5">
              <BrandLogo alt="IHTHISABI" size="sm" />
              <div className="min-w-0">
                <h1 className="break-words text-[15px] font-bold leading-tight tracking-tight text-gray-900 [overflow-wrap:anywhere]">
                  {pageTitle}
                </h1>
                <p className="flex flex-wrap items-baseline gap-x-1.5 text-xs leading-snug text-gray-600">
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">{contextLabel}</span>
                  {user?.ruknId && (
                    <span className="whitespace-nowrap">· RUKN ID <span className="font-semibold">{user.ruknId}</span></span>
                  )}
                </p>
              </div>
            </div>
            <div className="relative z-30 shrink-0">
              <button
                onClick={() => setProfileMenuOpen((prev) => !prev)}
                className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
                aria-label="Account menu"
              >
                <User className="h-[18px] w-[18px]" />
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 top-11 z-30 w-52 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
                  <PoweredByD4DX />
                  <button
                    type="button"
                    onClick={() => { setProfileMenuOpen(false); navigate('/ihthisabi/profile') }}
                    className="flex min-h-11 w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => { setProfileMenuOpen(false); navigate('/ihthisabi/help-desk') }}
                    className="flex min-h-11 w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <LifeBuoy className="h-4 w-4" />
                    Help Desk
                  </button>
                  {otherRoles.map((roleOption) => (
                    <button
                      key={roleOption.role}
                      onClick={() => handleSwitchRole(roleOption.role)}
                      disabled={switchingRole}
                      className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      <ArrowLeftRight className="w-4 h-4" />
                      <span className="truncate">Switch to {roleOption.label}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => { setProfileMenuOpen(false); handleLogout() }}
                    className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          {profileMenuOpen && (
            <div className="fixed inset-0 z-20" onClick={() => setProfileMenuOpen(false)} />
          )}
        </div>

        {/* Page content */}
        <main data-app-scroll className="app-scroll-region flex-1 ih-mobile-bottom-safe lg:pb-0">
          <Outlet />
        </main>

        {/* Mobile bottom nav — curated minimal destinations + More */}
        {bottomNavItems.length > 0 && (
          <>
          {moreOpen && (
            <div
              className="lg:hidden fixed inset-0 z-30 bg-gray-900/40"
              onClick={() => setMoreOpen(false)}
            />
          )}
          <div className="lg:hidden fixed inset-x-0 bottom-0 z-30 bg-white/80 backdrop-blur-xl backdrop-saturate-150 shadow-[0_-1px_0_rgba(16,24,40,0.06),0_-8px_32px_rgba(16,24,40,0.06)] ih-mobile-nav-safe">
            {/* "More" panel — grows upward out of the bar, carrying only the
                destinations the bar itself doesn't already show. */}
            {moreOpen && (
              <div ref={moreSheetRef} id="ihthisabi-mobile-more-sheet" role="dialog" aria-modal="true" aria-label="More navigation" className="ih-more-sheet max-h-[60vh] overflow-y-auto border-b border-gray-200 bg-white px-3 pb-2 pt-3">
                {moreItems.length > 0 && (
                  <div className="space-y-1">
                    {moreItems.map(item => {
                      const Icon = item.icon
                      const active = isActive(item.href)
                      return (
                        <button
                          key={item.href}
                          onClick={() => { setMoreOpen(false); navigate(item.href) }}
                          className={`flex w-full min-h-[52px] items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors ${
                            active ? 'bg-[#7B4FF2] text-white' : 'text-gray-700 active:bg-gray-100'
                          }`}
                        >
                          <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-white' : 'text-gray-400'}`} />
                          <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        </button>
                      )
                    })}
                  </div>
                )}

                {otherRoles.length > 0 && (
                  <div className="mt-2 border-t border-gray-100 pt-2">
                    <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Switch Role
                    </p>
                    {otherRoles.map(roleOption => (
                      <button
                        key={roleOption.role}
                        onClick={() => { setMoreOpen(false); handleSwitchRole(roleOption.role) }}
                        disabled={switchingRole}
                        className="flex w-full min-h-[52px] items-center gap-3 rounded-xl px-3 text-left text-sm font-medium text-gray-700 transition-colors active:bg-gray-100 disabled:opacity-60"
                      >
                        <ArrowLeftRight className="h-[18px] w-[18px] shrink-0 text-gray-400" />
                        <span className="min-w-0 flex-1 truncate">
                          {roleOption.label}{roleOption.scope ? ` · ${roleOption.scope}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-2 border-t border-gray-100 pt-2">
                  <button
                    onClick={() => { setMoreOpen(false); handleLogout() }}
                    className="flex w-full min-h-[52px] items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold text-red-600 transition-colors active:bg-red-50"
                  >
                    <LogOut className="h-[18px] w-[18px] shrink-0" />
                    <span>Logout</span>
                  </button>
                  <PoweredByD4DX />
                </div>
              </div>
            )}
            <nav
              className="grid gap-2 px-3 py-1.5"
              style={{ gridTemplateColumns: `repeat(${bottomNavItems.length}, minmax(0, 1fr))` }}
            >
              {bottomNavItems.map((item) => {
                const Icon = item.icon
                const active = isBottomActive(item)
                return (
                  <button
                    key={item.name}
                    ref={item.action === 'menu' ? moreButtonRef : undefined}
                    onClick={() => handleBottomClick(item)}
                    className={`relative flex min-h-[52px] min-w-0 flex-col items-center justify-center gap-1 rounded-xl border px-2 py-1.5 text-xs font-semibold leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-inset ${
                      active ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    aria-current={active ? 'page' : undefined}
                    aria-haspopup={item.action === 'menu' ? 'dialog' : undefined}
                    aria-expanded={item.action === 'menu' ? moreOpen : undefined}
                    aria-controls={item.action === 'menu' ? 'ihthisabi-mobile-more-sheet' : undefined}
                  >
                    <span
                      className="flex h-5 w-5 items-center justify-center"
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                    </span>
                    <span className="max-w-full break-words text-center [overflow-wrap:anywhere]">{item.name}</span>
                  </button>
                )
              })}
            </nav>
          </div>
          </>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  )
}

export default Layout
